import React, { useState, useEffect } from 'react';
import { DownloadSimple, MagnifyingGlass, ArrowLeft, WarningCircle, CaretRight, ArrowRight } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import authedFetch from '../lib/authedFetch';

// Papéis semânticos do Care Design System v3 (nunca primitivos nem hex).
// Botões, campo, segmentado, cards, KPIs, pílulas e esqueletos vêm do kit (src/styles/care-ui.css).
const C = {
  surface: 'var(--care-bg-surface)',
  textPrimary: 'var(--care-text-primary)',
  textSecondary: 'var(--care-text-secondary)',
  textTertiary: 'var(--care-text-tertiary)',
  borderSubtle: 'var(--care-border-subtle)',
  neutralText: 'var(--care-neutral-text)',
  fontMono: 'var(--care-font-mono)',
};

// Cabeçalho de tabela do CareOS: 12px 600, fio --border, sem quebra
const thStyle = {
  color: 'var(--muted-foreground)',
  borderBottom: '1px solid var(--border)',
  background: C.surface,
  whiteSpace: 'nowrap',
};
// Coluna Cliente fixa ao rolar a tabela na horizontal (celular)
const stickyCol = { position: 'sticky', left: 0, zIndex: 'var(--care-z-sticky)', background: C.surface };
const cellBorder = { borderBottom: `1px solid ${C.borderSubtle}` };
// Conta e Patrimônio somem abaixo de sm
const HIDE_SM = 'hidden sm:table-cell';
const NUM_COLS = [
  { w: 'w-40', align: 'left' }, { w: 'w-16', align: 'left', hide: true }, { w: 'w-12', align: 'left' },
  { w: 'w-24', align: 'right' }, { w: 'w-20', align: 'right' }, { w: 'w-16', align: 'right' },
  { w: 'w-24', align: 'right' }, { w: 'w-24', align: 'right', hide: true }, { w: 'w-10', align: 'right' },
];

// KPI do kit: rótulo 12px, valor 25px tabular (−0.01em), contagem em .ui-meta
const Kpi = ({ rotulo, valor, contagem, loading }) => (
  <div className="ui-card">
    <div className="ui-kpi">
      <span className="ui-kpi__rotulo">{rotulo}</span>
      <span className="ui-kpi__valor">
        {loading ? <span className="skel inline-block h-7 w-32" aria-hidden="true" /> : valor}
      </span>
      <span className="ui-meta">
        {loading ? <span className="skel inline-block h-3 w-16" aria-hidden="true" /> : <>{contagem} clientes</>}
      </span>
    </div>
  </div>
);

// Traço para célula sem posição: visual "—", leitor de tela "sem posição"
const SemPosicao = () => (
  <><span aria-hidden="true">—</span><span className="sr-only">sem posição</span></>
);

const AcompanhamentoSemanal = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [erroDownload, setErroDownload] = useState(null);
  const [dataRelatorio, setDataRelatorio] = useState('');
  const [valorMinimo, setValorMinimo] = useState(0);
  const [corretoraFiltro, setCorretoraFiltro] = useState('todos'); // 'todos', 'btg', 'xp'

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    buscarDados();
  }, []);

  const buscarDados = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authedFetch(`${API_URL}/api/patrimonio/acompanhamento`);
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const data = await res.json();
      // Ordenar por total_monitorado DESC (maiores valores primeiro).
      // Guard: sem data.clientes o erro virava "Nenhum cliente encontrado" + R$ 0,00.
      const clientesOrdenados = [...(data.clientes || [])].sort((a, b) => b.total_monitorado - a.total_monitorado);
      setClientes(clientesOrdenados);
      setDataRelatorio(data.data_relatorio);
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError(err.message || 'Falha ao carregar');
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    setDownloading(true);
    setErroDownload(null);
    try {
      const res = await authedFetch(`${API_URL}/api/patrimonio/download`);

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `patrimonio_${dataRelatorio || 'latest'}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setErroDownload(`Erro ${res.status}`);
      }
    } catch (err) {
      console.error('Erro:', err);
      setErroDownload(err.message || 'Falha no download');
    } finally {
      setDownloading(false);
    }
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    }).format(valor);
  };

  const clientesFiltrados = clientes.filter(c =>
    (c.nome.toLowerCase().includes(search.toLowerCase()) ||
    c.conta.includes(search)) &&
    c.total_monitorado >= valorMinimo &&
    (corretoraFiltro === 'todos' || c.corretora.toLowerCase() === corretoraFiltro)
  );

  const totalRV = clientesFiltrados.reduce((sum, c) => sum + c.rv_internacional, 0);
  const totalCripto = clientesFiltrados.reduce((sum, c) => sum + c.cripto, 0);
  const totalOuro = clientesFiltrados.reduce((sum, c) => sum + c.ouro, 0);
  const totalGeral = totalRV + totalCripto + totalOuro;

  // Contar por corretora
  const countBTG = clientes.filter(c => c.corretora === 'BTG').length;
  const countXP = clientes.filter(c => c.corretora === 'XP').length;

  const opcoesValor = [
    { label: 'Todos', valor: 0 },
    { label: '> R$ 50k', valor: 50000 },
    { label: '> R$ 100k', valor: 100000 },
    { label: '> R$ 200k', valor: 200000 },
    { label: '> R$ 500k', valor: 500000 },
  ];

  const limparFiltros = () => {
    setSearch('');
    setValorMinimo(0);
    setCorretoraFiltro('todos');
  };

  const thClass = 'py-3 px-4 text-xs font-semibold';

  return (
    <div className={embedded ? undefined : 'ui-pagina'}>
      {/* Header */}
      {!embedded && (
        <div className="mb-8">
          <button
            type="button"
            className="ui-botao mb-4"
            data-variante="fantasma"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft size={16} weight="bold" aria-hidden="true" />
            Voltar ao Dashboard
          </button>
          <header className="ui-tela" style={{ marginBottom: 0 }}>
            <div className="ui-tela__texto">
              <h1 className="ui-tela__titulo">Acompanhamento Semanal</h1>
              <p className="ui-tela__sub">Monitoramento de RV Internacional, Criptoativos e Ouro</p>
              {dataRelatorio && (
                <span className="ui-meta">
                  Dados de: <span className="tabular-nums lining-nums">{new Date(dataRelatorio + 'T00:00:00').toLocaleDateString('pt-BR')}</span>
                </span>
              )}
            </div>
          </header>
        </div>
      )}

      {/* Erro de carregamento (antes virava "Nenhum cliente encontrado" + R$ 0,00) */}
      {error && !loading && (
        <div role="alert" className="ui-pilha mb-6" data-gap="4" style={{ alignItems: 'flex-start' }}>
          <p className="ui-aviso" style={{ color: 'var(--destructive)', margin: 0 }}>
            <WarningCircle size={16} weight="bold" aria-hidden="true" />
            Não foi possível carregar os dados
          </p>
          <details className="ui-detalhe" style={{ marginTop: 0 }}>
            <summary className="ui-detalhe__resumo">
              <span className="ui-detalhe__chevron"><CaretRight size={12} weight="bold" aria-hidden="true" /></span>
              Ver detalhe
            </summary>
            <div className="ui-detalhe__corpo">{error}</div>
          </details>
          <button type="button" className="ui-botao" data-variante="fio" onClick={buscarDados}>
            Tentar de novo
          </button>
        </div>
      )}

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <Kpi
          rotulo="RV Internacional"
          valor={formatarMoeda(totalRV)}
          contagem={clientesFiltrados.filter(c => c.rv_internacional > 0).length}
          loading={loading}
        />
        <Kpi
          rotulo="Criptoativos"
          valor={formatarMoeda(totalCripto)}
          contagem={clientesFiltrados.filter(c => c.cripto > 0).length}
          loading={loading}
        />
        <Kpi
          rotulo="Ouro (GOLD11)"
          valor={formatarMoeda(totalOuro)}
          contagem={clientesFiltrados.filter(c => c.ouro > 0).length}
          loading={loading}
        />
        <Kpi
          rotulo="Total Monitorado"
          valor={formatarMoeda(totalGeral)}
          contagem={clientesFiltrados.length}
          loading={loading}
        />
      </div>

      {/* Ferramentas */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-1 flex-wrap">
          <div className="relative max-w-md flex-1 min-w-[200px]" style={{ color: C.textTertiary }}>
            <MagnifyingGlass
              size={20}
              aria-hidden="true"
              className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Buscar cliente..."
              aria-label="Buscar cliente"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ui-campo w-full"
              style={{ paddingLeft: 40 }}
            />
          </div>

          {/* Filtro por Corretora */}
          <div className="ui-segmentado" role="group" aria-label="Filtrar por corretora">
            <button
              type="button"
              className="ui-segmentado__opcao"
              onClick={() => setCorretoraFiltro('todos')}
              aria-pressed={corretoraFiltro === 'todos'}
            >
              Todos (<span className="tabular-nums lining-nums">{clientes.length}</span>)
            </button>
            <button
              type="button"
              className="ui-segmentado__opcao"
              onClick={() => setCorretoraFiltro('btg')}
              aria-pressed={corretoraFiltro === 'btg'}
            >
              BTG (<span className="tabular-nums lining-nums">{countBTG}</span>)
            </button>
            <button
              type="button"
              className="ui-segmentado__opcao"
              onClick={() => setCorretoraFiltro('xp')}
              aria-pressed={corretoraFiltro === 'xp'}
            >
              XP (<span className="tabular-nums lining-nums">{countXP}</span>)
            </button>
          </div>

          {/* Filtro por Valor */}
          <div className="ui-segmentado" role="group" aria-label="Filtrar por valor mínimo">
            {opcoesValor.map((opcao) => (
              <button
                key={opcao.valor}
                type="button"
                className="ui-segmentado__opcao"
                onClick={() => setValorMinimo(opcao.valor)}
                aria-pressed={valorMinimo === opcao.valor}
              >
                {opcao.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-4">
            <span className="text-[13px]" style={{ color: C.textSecondary }}>
              Exibindo <span className="font-semibold tabular-nums lining-nums" style={{ color: C.textPrimary }}>{clientesFiltrados.length}</span> de <span className="font-semibold tabular-nums lining-nums" style={{ color: C.textPrimary }}>{clientes.length}</span> clientes
            </span>

            <button
              type="button"
              className="ui-botao"
              data-variante="solido"
              onClick={downloadExcel}
              disabled={downloading}
              aria-busy={downloading}
              aria-describedby={erroDownload ? 'erro-download' : undefined}
            >
              <DownloadSimple size={16} weight="bold" aria-hidden="true" />
              {downloading ? 'Gerando…' : 'Baixar Excel'}
            </button>
          </div>
          {erroDownload && (
            <p id="erro-download" role="alert" className="ui-aviso" style={{ color: 'var(--destructive)', margin: 0 }}>
              <WarningCircle size={16} weight="bold" aria-hidden="true" />
              Não foi possível gerar o Excel ({erroDownload})
            </p>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="ui-card" style={{ padding: 0, gap: 0 }}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] sm:min-w-[880px]" style={{ fontVariantNumeric: 'tabular-nums lining-nums' }}>
            <thead>
              <tr>
                <th scope="col" className={`text-left ${thClass}`} style={{ ...thStyle, ...stickyCol }}>Cliente</th>
                <th scope="col" className={`text-left ${thClass} ${HIDE_SM}`} style={thStyle}>Conta</th>
                <th scope="col" className={`text-left ${thClass}`} style={thStyle}>Corretora</th>
                <th scope="col" className={`text-right ${thClass}`} style={thStyle}>RV Internacional</th>
                <th scope="col" className={`text-right ${thClass}`} style={thStyle}>Cripto</th>
                <th scope="col" className={`text-right ${thClass}`} style={thStyle}>Ouro</th>
                <th scope="col" className={`text-right ${thClass}`} style={thStyle}>Total</th>
                <th scope="col" className={`text-right ${thClass} ${HIDE_SM}`} style={thStyle}>Patrimônio</th>
                <th scope="col" className={`text-right ${thClass}`} style={thStyle}>%</th>
              </tr>
            </thead>
            <tbody aria-busy={loading}>
              {loading ? (
                [0, 1, 2, 3, 4, 5].map((r) => (
                  <tr key={`skel-${r}`} aria-hidden="true">
                    {NUM_COLS.map((col, ci) => (
                      <td
                        key={ci}
                        className={`py-3 px-4${col.hide ? ` ${HIDE_SM}` : ''}`}
                        style={{ textAlign: col.align, ...cellBorder, ...(ci === 0 ? stickyCol : null) }}
                      >
                        <span className={`skel inline-block h-3 ${col.w}`} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : clientesFiltrados.length === 0 ? (
                // Com erro de carga o aviso acima já explica; não repetir "nenhum cliente"
                error ? null : (
                  <tr>
                    <td colSpan={9}>
                      <div className="ui-vazio-bloco" data-area>
                        <p className="ui-vazio">
                          {clientes.length ? 'Nenhum cliente com estes filtros' : 'Nenhum cliente encontrado'}
                        </p>
                        {clientes.length > 0 && (
                          <button type="button" className="ui-vazio__caminho" onClick={limparFiltros}>
                            Limpar filtros <ArrowRight size={16} weight="bold" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              ) : (
                clientesFiltrados.map((cliente, idx) => (
                  <tr key={cliente.conta ? `${cliente.corretora}-${cliente.conta}` : idx}>
                    <td
                      className="py-3 px-4 text-[15px] font-medium"
                      style={{ color: C.textPrimary, ...cellBorder, ...stickyCol }}
                    >
                      {cliente.nome}
                    </td>
                    <td
                      className={`py-3 px-4 text-[13px] ${HIDE_SM}`}
                      style={{ color: C.textSecondary, fontFamily: C.fontMono, ...cellBorder }}
                    >
                      {cliente.conta}
                    </td>
                    <td className="py-3 px-4 text-left" style={cellBorder}>
                      <span className="ui-pilula" data-tom="forte">{cliente.corretora}</span>
                    </td>
                    <td
                      className="py-3 px-4 text-[15px] text-right font-normal"
                      style={{ color: cliente.rv_internacional > 0 ? C.textPrimary : C.neutralText, ...cellBorder }}
                    >
                      {cliente.rv_internacional > 0 ? formatarMoeda(cliente.rv_internacional) : <SemPosicao />}
                    </td>
                    <td
                      className="py-3 px-4 text-[15px] text-right font-normal"
                      style={{ color: cliente.cripto > 0 ? C.textPrimary : C.neutralText, ...cellBorder }}
                    >
                      {cliente.cripto > 0 ? formatarMoeda(cliente.cripto) : <SemPosicao />}
                    </td>
                    <td
                      className="py-3 px-4 text-[15px] text-right font-normal"
                      style={{ color: cliente.ouro > 0 ? C.textPrimary : C.neutralText, ...cellBorder }}
                    >
                      {cliente.ouro > 0 ? formatarMoeda(cliente.ouro) : <SemPosicao />}
                    </td>
                    <td
                      className="py-3 px-4 text-[15px] text-right font-semibold"
                      style={{ color: C.textPrimary, ...cellBorder }}
                    >
                      {formatarMoeda(cliente.total_monitorado)}
                    </td>
                    <td
                      className={`py-3 px-4 text-[15px] text-right ${HIDE_SM}`}
                      style={{ color: C.textSecondary, ...cellBorder }}
                    >
                      {formatarMoeda(cliente.patrimonio_total)}
                    </td>
                    <td className="py-3 px-4 text-right" style={cellBorder}>
                      {(() => {
                        const pct = cliente.percentual ?? 0;
                        const nivel = pct > 30 ? 'alta' : pct > 15 ? 'média' : 'baixa';
                        const pctFmt = pct.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                        // Severidade ordinal em box CSS (§4.4): ●○○ até 15%, ●●○ até 30%, ●●● acima
                        return (
                          <span className="ui-pilula" data-tom={pct > 30 ? 'negativo' : pct > 15 ? 'atencao' : undefined}>
                            <span className="sev" aria-hidden="true">
                              <i />
                              <i data-vazio={pct <= 15 || undefined} />
                              <i data-vazio={pct <= 30 || undefined} />
                            </span>
                            {pctFmt}%
                            <span className="sr-only">, concentração {nivel}</span>
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AcompanhamentoSemanal;
